import React, { useEffect, useRef, useState } from 'react';
import CalendarDay from '@app/state/calendar/CalendarDay';
import {
	useMarketStateTools,
} from '@app/site/secured/rateshopper/rates/dashboard/fragment/__market-state/common/hook/useMarketStateTools';
import ModalHandler from '@app/component/overlays/modal/ModalHandler';
import {
	MarketStateSnapshotCalendarDayTooltip,
} from '@app/site/secured/rateshopper/rates/dashboard/fragment/__market-state/common/tooltip/MarketStateSnapshotCalendarDayTooltip';
import MarketStateSnapshotCalendarDayTable, {
	type MarketStateSnapshotCalendarDayTableCompetitorSnapshots,
}
	from '@app/site/secured/rateshopper/rates/dashboard/fragment/__market-state/common/tooltip/MarketStateSnapshotCalendarDayTable';
import MarketStateDayPriceEvolutionModal
	from '@app/site/secured/rateshopper/rates/dashboard/fragment/__market-state/_calendar/_desktop/fragment/timeline/MarketStateDayPriceEvolutionModal';
import { PlacesType } from 'react-tooltip';
import MarketStateDailySnapshotWithComparisonOutput
	from '@app/model/market_state/output/MarketStateDailySnapshotWithComparisonOutput';

type MarketStateSnapshotCalendarDayTooltipRef = {
	open: () => void,
	close: () => void
};

export const MarketStateDailySnapshotTooltip: React.FunctionComponent<{
	calendarDay: CalendarDay,
	tooltipId: string,
	place: PlacesType,
	openRequestId: number,
	shouldLoad: boolean
}> = ({ calendarDay, tooltipId, place, openRequestId, shouldLoad }) => {
	const tools = useMarketStateTools();
	const tooltip = useRef<MarketStateSnapshotCalendarDayTooltipRef>(null);
	const timelineGraph = useRef<ModalHandler>(null);
	const findPreviousSnapshot = useRef(tools.findPreviousSnapshot);
	const lastOpenedRequestId = useRef<number | undefined>(undefined);
	const competitorSnapshotsCache = useRef<Map<string, MarketStateSnapshotCalendarDayTableCompetitorSnapshots>>(new Map());
	const [competitorSnapshots, setCompetitorSnapshots] = useState<MarketStateSnapshotCalendarDayTableCompetitorSnapshots | undefined>(undefined);

	useEffect(() => {
		findPreviousSnapshot.current = tools.findPreviousSnapshot;
	}, [tools.findPreviousSnapshot]);

	const onOpenTimelineGraph = () => {
		if (timelineGraph.current !== null && tooltip.current !== null) {
			tooltip.current.close();
			timelineGraph.current.open();
		}
	};

	const marketStateDailySnapshot: MarketStateDailySnapshotWithComparisonOutput | undefined
		= tools.findDailySnapshotState(calendarDay.display)?.marketStateDailySnapshot;
	const competitorSnapshotsCacheKey = `${calendarDay.display}_${marketStateDailySnapshot?.current.latestUpdateDate ?? ''}`;

	useEffect(() => {
		let isActive = true;
		setCompetitorSnapshots(undefined);

		if (!shouldLoad || !marketStateDailySnapshot) return;

		const cachedSnapshots = competitorSnapshotsCache.current.get(competitorSnapshotsCacheKey);
		if (cachedSnapshots) {
			setCompetitorSnapshots(cachedSnapshots);
			return;
		}

		marketStateDailySnapshot.getCompetitorSnapshotsComparison(findPreviousSnapshot.current)
			.then(snapshots => {
				competitorSnapshotsCache.current.set(competitorSnapshotsCacheKey, snapshots);
				if (isActive) setCompetitorSnapshots(snapshots);
			})
			.catch(() => {
				if (isActive) setCompetitorSnapshots([]);
			});

		return () => {
			isActive = false;
		};
	}, [competitorSnapshotsCacheKey, marketStateDailySnapshot, shouldLoad]);

	useEffect(() => {
		if (!shouldLoad || competitorSnapshots === undefined || lastOpenedRequestId.current === openRequestId) return;

		tooltip.current?.open();
		lastOpenedRequestId.current = openRequestId;
	}, [competitorSnapshots, openRequestId, shouldLoad]);


	return <>

		<MarketStateSnapshotCalendarDayTooltip id={tooltipId} ref={tooltip} place={place} disabled={competitorSnapshots === undefined}>
			{shouldLoad && !!marketStateDailySnapshot && competitorSnapshots !== undefined &&
				<MarketStateSnapshotCalendarDayTable
					calendarDay={calendarDay}
					marketStateDailySnapshot={marketStateDailySnapshot}
					competitorSnapshots={competitorSnapshots}
					onOpenTimelineGraph={onOpenTimelineGraph}
				/>}
		</MarketStateSnapshotCalendarDayTooltip>
		<MarketStateDayPriceEvolutionModal ref={timelineGraph} calendarDay={calendarDay} />
	</>;
};
